import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CommunityEvent } from '@/entities/community-event';
import { ImageShape } from '../lib/types';
import { formatDate } from '../lib/utils/dates/index';
import { axiosAPI } from './axios-instance';
import { ApiRequestFnParams, ApiResponse } from './lib/types';

export type ApiCommunityEventsRequest = Partial<{
    limit: number;
    page: number;
    show_main: 0 | 1;
    type: 'past' | 'future';
}>;

type Params = ApiRequestFnParams<ApiCommunityEventsRequest>;

export type ApiCommunityEventsResponse = ApiResponse<{
    totalCount: number;
    items: {
        id: number;
        slug: string;
        name: string;
        main: boolean;
        description?: string;
        location?: string;
        date_from?: string;
        date_to: string;
        show_detail: boolean;
        image?: ImageShape;
    }[];
}>;

export type AppCommunityEventsResponse = ApiResponse<{
    items: CommunityEvent[];
    totalCount: number;
}>;

const transformApiResponse = (response: ApiCommunityEventsResponse): AppCommunityEventsResponse => {
    return {
        ...response,
        data: {
            totalCount: response.data?.totalCount || 0,
            items: (response.data?.items ?? []).map((item) => ({
                slug: item.slug,
                img: item.image || null,
                title: item.name,
                description: item.description || null,
                startDate: item.date_from ? formatDate(new Date(item.date_from)) : formatDate(new Date(item.date_to)),
                endDate: formatDate(new Date(item.date_to)),
                location: item.location || null,
                hasDetail: item.show_detail,
                redirectToMainPage: item.main,
            })),
        },
    };
};

export const getCommunityEvents = ({ params = {} }: Params = {}): Promise<AppCommunityEventsResponse> =>
    axiosAPI.get<ApiCommunityEventsResponse>('/v1/events', { params }).then((res) => transformApiResponse(res.data));

export const useCommunityEventsQuery = (data: Params, initialData?: AppCommunityEventsResponse) =>
    useQuery({
        queryKey: ['community-events', data],
        queryFn: () => getCommunityEvents(data),
        initialData,
        placeholderData: keepPreviousData,
    });
